import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuthStore } from '../stores/auth'
import {
  User,
  Mail,
  Shield,
  Palette,
  Save,
  ArrowLeft,
  CheckCircle,
  AlertTriangle,
  Eye,
  EyeOff
} from 'lucide-react'

export const UserSettings: React.FC = () => {
  const navigate = useNavigate()
  const { user, updateProfile, isAuthenticated } = useAuthStore()
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)

  const [formData, setFormData] = useState({
    username: '',
    full_name: '',
    email: '',
    current_password: '',
    new_password: '',
    confirm_password: '',
    theme: 'light' as 'light' | 'dark',
    default_model: '@replicate/black-forest-labs/flux-schnell',
    max_image_size: '1024x1024',
    auto_optimize_prompt: true,
    email_notifications: true,
  })

  // 检查认证状态
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth')
      return
    }
  }, [isAuthenticated, navigate])

  // 加载用户数据
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        username: user.username || '',
        full_name: user.full_name || '',
        email: user.email,
      }))
    }
  }, [user])

  const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }))
  }

  const handleCheckboxChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [field]: e.target.checked }))
  }

  const handleSaveProfile = async () => {
    setSaving(true)
    setMessage(null)

    try {
      await updateProfile({
        username: formData.username,
        full_name: formData.full_name,
      })

      setMessage({ type: 'success', text: '个人资料已更新' })
    } catch (error) {
      setMessage({ type: 'error', text: error instanceof Error ? error.message : '更新失败' })
    } finally {
      setSaving(false)
    }
  }

  const handleChangePassword = async () => {
    if (formData.new_password !== formData.confirm_password) {
      setMessage({ type: 'error', text: '新密码和确认密码不匹配' })
      return
    }

    if (formData.new_password.length < 6) {
      setMessage({ type: 'error', text: '新密码至少需要6个字符' })
      return
    }

    setSaving(true)
    setMessage(null)

    try {
      // 这里应该调用密码更改API
      // 暂时模拟成功
      await new Promise(resolve => setTimeout(resolve, 1000))

      setMessage({ type: 'success', text: '密码已更改' })

      // 清空密码字段
      setFormData(prev => ({
        ...prev,
        current_password: '',
        new_password: '',
        confirm_password: '',
      }))
    } catch (error) {
      setMessage({ type: 'error', text: error instanceof Error ? error.message : '密码更改失败' })
    } finally {
      setSaving(false)
    }
  }

  const handleSavePreferences = async () => {
    setSaving(true)
    setMessage(null)

    try {
      // 保存偏好设置到localStorage
      const preferences = {
        theme: formData.theme,
        default_model: formData.default_model,
        max_image_size: formData.max_image_size,
        auto_optimize_prompt: formData.auto_optimize_prompt,
        email_notifications: formData.email_notifications,
      }

      localStorage.setItem('user-preferences', JSON.stringify(preferences))

      setMessage({ type: 'success', text: '偏好设置已保存' })
    } catch (error) {
      setMessage({ type: 'error', text: '设置保存失败' })
    } finally {
      setSaving(false)
    }
  }

  // 加载偏好设置
  useEffect(() => {
    const savedPreferences = localStorage.getItem('user-preferences')
    if (savedPreferences) {
      try {
        const preferences = JSON.parse(savedPreferences)
        setFormData(prev => ({ ...prev, ...preferences }))
      } catch (error) {
        console.error('Failed to load preferences:', error)
      }
    }
  }, [])

  return (
    <div className="min-h-screen bg-[#0d1117]">
      {/* Header */}
      <header className="bg-[#161b22] border-b border-cyan-900/30">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link
                to="/"
                className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>返回</span>
              </Link>
              <div className="h-6 w-px bg-cyan-900/30"></div>
              <h1 className="text-xl font-semibold text-white">账户设置</h1>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Message */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg flex items-center space-x-3 ${
            message.type === 'success'
              ? 'bg-green-900/20 border border-green-500/30 text-green-400'
              : 'bg-red-900/20 border border-red-500/30 text-red-400'
          }`}>
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <AlertTriangle className="w-5 h-5" />
            )}
            <span>{message.text}</span>
          </div>
        )}

        <div className="space-y-8">
          {/* Profile Settings */}
          <div className="bg-[#161b22] rounded-xl p-6 border border-cyan-900/30">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-cyan-900/30 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">个人资料</h3>
                <p className="text-sm text-gray-400">更新您的个人信息</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-300 mb-2">
                  用户名
                </label>
                <input
                  type="text"
                  id="username"
                  value={formData.username}
                  onChange={handleInputChange('username')}
                  className="w-full px-3 py-2 bg-[#0d1117] border border-cyan-900/30 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  placeholder="您的用户名"
                />
              </div>

              <div>
                <label htmlFor="full_name" className="block text-sm font-medium text-gray-300 mb-2">
                  全名
                </label>
                <input
                  type="text"
                  id="full_name"
                  value={formData.full_name}
                  onChange={handleInputChange('full_name')}
                  className="w-full px-3 py-2 bg-[#0d1117] border border-cyan-900/30 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  placeholder="您的全名"
                />
              </div>

              <div className="md:col-span-2">
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                  邮箱地址
                </label>
                <div className="relative">
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={handleInputChange('email')}
                    className="w-full pl-10 pr-3 py-2 bg-[#0d1117] border border-cyan-900/30 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    placeholder="your@email.com"
                  />
                  <Mail className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {user?.email_verified ? '邮箱已验证' : '邮箱未验证'}
                </p>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={handleSaveProfile}
                disabled={saving}
                className="flex items-center space-x-2 px-6 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-md hover:from-cyan-600 hover:to-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <Save className="w-4 h-4" />
                )}
                <span>保存更改</span>
              </button>
            </div>
          </div>

          {/* Password Change */}
          <div className="bg-[#161b22] rounded-xl p-6 border border-cyan-900/30">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-red-900/30 rounded-full flex items-center justify-center">
                <Shield className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">更改密码</h3>
                <p className="text-sm text-gray-400">更新您的账户密码</p>
              </div>
            </div>

            <div className="space-y-4 max-w-md">
              <div>
                <label htmlFor="current_password" className="block text-sm font-medium text-gray-300 mb-2">
                  当前密码
                </label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    id="current_password"
                    value={formData.current_password}
                    onChange={handleInputChange('current_password')}
                    className="w-full pr-10 px-3 py-2 bg-[#0d1117] border border-cyan-900/30 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    placeholder="输入当前密码"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-200"
                  >
                    {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="new_password" className="block text-sm font-medium text-gray-300 mb-2">
                  新密码
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    id="new_password"
                    value={formData.new_password}
                    onChange={handleInputChange('new_password')}
                    className="w-full pr-10 px-3 py-2 bg-[#0d1117] border border-cyan-900/30 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    placeholder="输入新密码"
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-200"
                  >
                    {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="confirm_password" className="block text-sm font-medium text-gray-300 mb-2">
                  确认新密码
                </label>
                <input
                  type="password"
                  id="confirm_password"
                  value={formData.confirm_password}
                  onChange={handleInputChange('confirm_password')}
                  className="w-full px-3 py-2 bg-[#0d1117] border border-cyan-900/30 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  placeholder="再次输入新密码"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={handleChangePassword}
                disabled={saving}
                className="flex items-center space-x-2 px-6 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-md hover:from-red-600 hover:to-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <Shield className="w-4 h-4" />
                )}
                <span>更改密码</span>
              </button>
            </div>
          </div>

          {/* Preferences */}
          <div className="bg-[#161b22] rounded-xl p-6 border border-cyan-900/30">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-purple-900/30 rounded-full flex items-center justify-center">
                <Palette className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">偏好设置</h3>
                <p className="text-sm text-gray-400">自定义您的使用体验</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="theme" className="block text-sm font-medium text-gray-300 mb-2">
                  主题
                </label>
                <select
                  id="theme"
                  value={formData.theme}
                  onChange={handleInputChange('theme')}
                  className="w-full px-3 py-2 bg-[#0d1117] border border-cyan-900/30 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                >
                  <option value="light">浅色</option>
                  <option value="dark">深色</option>
                </select>
              </div>

              <div>
                <label htmlFor="default_model" className="block text-sm font-medium text-gray-300 mb-2">
                  默认模型
                </label>
                <select
                  id="default_model"
                  value={formData.default_model}
                  onChange={handleInputChange('default_model')}
                  className="w-full px-3 py-2 bg-[#0d1117] border border-cyan-900/30 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                >
                  <option value="@replicate/black-forest-labs/flux-schnell">FLUX Schnell</option>
                  <option value="@replicate/black-forest-labs/flux-dev">FLUX Dev</option>
                </select>
              </div>

              <div>
                <label htmlFor="max_image_size" className="block text-sm font-medium text-gray-300 mb-2">
                  默认图像尺寸
                </label>
                <select
                  id="max_image_size"
                  value={formData.max_image_size}
                  onChange={handleInputChange('max_image_size')}
                  className="w-full px-3 py-2 bg-[#0d1117] border border-cyan-900/30 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                >
                  <option value="512x512">512x512</option>
                  <option value="768x768">768x768</option>
                  <option value="1024x1024">1024x1024</option>
                  <option value="1280x1280">1280x1280</option>
                </select>
              </div>

              <div className="space-y-3">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="auto_optimize_prompt"
                    checked={formData.auto_optimize_prompt}
                    onChange={handleCheckboxChange('auto_optimize_prompt')}
                    className="h-4 w-4 text-cyan-500 focus:ring-cyan-500 border-cyan-900/30 rounded"
                  />
                  <label htmlFor="auto_optimize_prompt" className="ml-2 text-sm text-gray-300">
                    自动优化提示词
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="email_notifications"
                    checked={formData.email_notifications}
                    onChange={handleCheckboxChange('email_notifications')}
                    className="h-4 w-4 text-cyan-500 focus:ring-cyan-500 border-cyan-900/30 rounded"
                  />
                  <label htmlFor="email_notifications" className="ml-2 text-sm text-gray-300">
                    邮件通知
                  </label>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={handleSavePreferences}
                disabled={saving}
                className="flex items-center space-x-2 px-6 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-md hover:from-green-600 hover:to-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <Save className="w-4 h-4" />
                )}
                <span>保存设置</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
